import type { WorkflowStatus } from "@entrilia/shared/api/workflowTypes";
import type { EntityFieldType } from "../../../../api/types/objectTypes";
import type {
  ActivityExecutionPhase,
  SearchWorkflowsRequest,
  Workflow,
  WorkflowFieldSchemaDefinition,
} from "../../../../api/types/workflowTypes";
import type { FilterState } from "../../../common/filters/filterState";
import type { TableFilter, TableFilterDefinition } from "../../../common/filters/filterTypes";
import { mapTableFilterToApiObjectFilter } from "../../../common/filters/objectFilterMapper";

const staticFilterDefinitions: TableFilterDefinition<Workflow>[] = [
  {
    id: "createdAt",
    name: "Created at",
    type: "date",
    operatorOptions: ["range"],
  },
  {
    id: "completedAt",
    name: "Completed at",
    type: "date",
    operatorOptions: ["range"],
  },
];

const fieldTypeToFilterTypeMap: Partial<Record<EntityFieldType, TableFilterDefinition<Workflow>["type"]>> = {
  Date: "date",
  DateTime: "date",
  Text: "text",
  Number: "number",
  Money: "number",
  Percent: "number",
  Checkbox: "boolean",
};

const getFilterDefinitionFromField = (
  fieldDefinition: WorkflowFieldSchemaDefinition
): TableFilterDefinition<Workflow> => ({
  id: fieldDefinition.id,
  name: fieldDefinition.displayName,
  type: fieldTypeToFilterTypeMap[fieldDefinition.type] ?? "text", // Currently not supporting select and lookup fields, can be expanded in the future as needed
});

export const getWorkflowRunsFilterDefinitions = (
  workflowFieldsSchema: WorkflowFieldSchemaDefinition[]
): TableFilterDefinition<Workflow>[] => {
  return [...staticFilterDefinitions, ...workflowFieldsSchema.map(getFilterDefinitionFromField)];
};

const getDateRangeFilterValues = (filter: TableFilter<Workflow>) => {
  if (filter.type !== "date") {
    return [undefined, undefined];
  }

  if (filter?.value?.operator !== "range") {
    return [undefined, undefined];
  }

  const { range } = filter.value;
  const from = range?.[0];
  const to = range?.[1];
  return [from || undefined, to || undefined];
};

export const getWorkflowsFilterPayload = (
  filterState: FilterState<Workflow>,
  statuses: WorkflowStatus[],
  templateId: string,
  currentActivityExecutionPhase?: ActivityExecutionPhase
): Omit<SearchWorkflowsRequest, "range"> => {
  const payload: Omit<SearchWorkflowsRequest, "range"> = {
    templateIds: [templateId],
    statuses,
    currentActivityExecutionPhase,
  };

  for (const filter of filterState.visibleFilters) {
    if (filter.id === "createdAt") {
      const [createdAtFrom, createdAtTo] = getDateRangeFilterValues(filter);
      payload.createdAtFrom = createdAtFrom;
      payload.createdAtTo = createdAtTo;
    } else if (filter.id === "completedAt") {
      const [completedAtFrom, completedAtTo] = getDateRangeFilterValues(filter);
      payload.completedAtFrom = completedAtFrom;
      payload.completedAtTo = completedAtTo;
    } else {
      const objectFilter = mapTableFilterToApiObjectFilter(filter);
      if (objectFilter) {
        if (!payload.workflowFieldsFilters) {
          payload.workflowFieldsFilters = [];
        }
        payload.workflowFieldsFilters.push(objectFilter);
      }
    }
  }

  return payload;
};
